import { collection, getDocs, query, Timestamp, where } from "firebase/firestore";
import { db } from "../firebase";
import { authErrorFields } from "./fields";

export function randomGenerator() {
    const chars = [
        'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j',
        'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't',
        'u', 'v', 'w', 'x', 'y', 'z', '0', '1', '2', '3',
        '4', '5', '6', '7', '8', '9'
    ];

    let randomStr = "";
    for (let i = 0; i < 6; i++) {
        const choosen = chars[Math.floor(Math.random() * (chars.length - 1))]
        randomStr += choosen;
    }

    return randomStr;
}

export async function collectionHasDoc(colName: string, field: string, value: string) {
    try {
        const col = collection(db, colName);
        const q = query(col, where(field, "==", value));
        const documents = await getDocs(q);
        return !documents.empty;
    } catch (error) {
        console.log(error);
    }
}

// Slug público de anunciante (/advertisers/:slug): 6 caracteres aleatórios ainda não usados.
// Se a checagem falhar (collectionHasDoc devolve undefined), o slug é aceito — mesmo
// comportamento que findUser.tsx sempre teve.
export async function generateUniqueSlug() {
    let randomStr = "";
    while (true) {
        randomStr = randomGenerator();
        const hasDoc = await collectionHasDoc("users", "slug", randomStr);
        if (!hasDoc) {
            return randomStr;
        }
    }
}

export function formatPhoneNumber(phoneNumber: string | undefined) {
    if (!phoneNumber) {
        return phoneNumber
    }
    if (phoneNumber?.length === 11) {
        return `(${phoneNumber.slice(0, 2)}) ${phoneNumber.slice(2, 7)}-${phoneNumber.slice(7)}`;
    }
    if (phoneNumber?.length === 10) {
        return `(${phoneNumber.slice(0, 2)}) ${phoneNumber.slice(2, 6)}-${phoneNumber.slice(6)}`;
    }
    else {
        return `(${phoneNumber?.slice(0, 2)}) ${phoneNumber?.slice(2)}`;
    }
}

export function parseTimestamp(time: Date) {
    if (!time) {
        return null;
    }
    const day = String(time.getDate()).padStart(2, '0');
    const month = String(time.getMonth() + 1).padStart(2, '0');
    const year = time.getFullYear();

    return `${day}/${month}/${year}`;
}

export function parseFirebaseTimestamp(time: Timestamp) {
    if (!time) {
        return "N/A";
    }
    const date = new Date(time.seconds * 1000);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
}

export function createDate(dateString: string) {
    const parts = dateString.split("/");
    const month = parseInt(parts[1]) - 1;
    return new Date(parseInt(parts[2]), month, parseInt(parts[0]));
}

export function calculateRate(reviews: { rate: number }[]) {
    if (!reviews.length) {
        return undefined;
    }
    const sum = reviews.reduce((tot, item) => tot + item.rate, 0);
    return sum / reviews.length;
}

export function getURLParams(url: string) {
    const params = new URLSearchParams(new URL(url).search);
    let paramObj: { [key: string]: string } = {};

    params.forEach((value, key) => {
        paramObj[key] = value;
    });

    return paramObj;
};

export function getAbsoluteUrl(url: string) {
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
        return `https://${url}`;
    }
    return url;
};

export function validateCPF(cpf: string) {
    // remove caracteres não numéricos
    const cleanCPF = cpf.replace(/[^\d]/g, '');
    // verifica se tem 11 digitos
    if (cleanCPF.length !== 11) {
        return false;
    }
    // verifica se todos os numeros sao iguais
    if (/^(\d)\1+$/.test(cleanCPF)) {
        return false;
    }
    // calcula dígitos verificadores
    let sum = 0;
    for (let i = 0; i < 9; i++) {
        sum += parseInt(cleanCPF.charAt(i)) * (10 - i);
    }

    let remainder = 11 - (sum % 11);
    remainder = remainder === 10 || remainder === 11 ? 0 : remainder;
    if (remainder !== parseInt(cleanCPF.charAt(9))) {
        return false;
    }

    sum = 0;
    for (let i = 0; i < 10; i++) {
        sum += parseInt(cleanCPF.charAt(i)) * (11 - i);
    }

    remainder = 11 - (sum % 11);
    remainder = remainder === 10 || remainder === 11 ? 0 : remainder;

    return remainder === parseInt(cleanCPF.charAt(10));
}

export function generateSearchTerms(brand: string, model: string) {

    const normalize = (text: string) =>
        text
            .toLowerCase()
            .trim()
            .replace(/\s+/g, " ");

    const terms = new Set<string>();

    const normalizedBrand = normalize(brand);
    const normalizedModel = normalize(model);

    // Texto completo: "tag heuer carrera"
    const fullText = `${normalizedBrand} ${normalizedModel}`.trim();

    // Gera prefixos do texto completo
    let fullPrefix = "";

    for (const char of fullText) {
        fullPrefix += char;
        terms.add(fullPrefix);
    }

    // Gera prefixos separados
    const texts = [normalizedBrand, normalizedModel];

    for (const text of texts) {

        if (!text) continue;

        // Prefixos do texto inteiro
        let prefix = "";

        for (const char of text) {
            prefix += char;
            terms.add(prefix);
        }

        // Prefixos das palavras individuais
        const words = text.split(" ");

        for (const word of words) {

            let wordPrefix = "";

            for (const char of word) {
                wordPrefix += char;
                terms.add(wordPrefix);
            }
        }
    }

    return Array.from(terms);
}

// Aceita "local@dominio.tld" com TLD de pelo menos 2 letras e rejeita
// casos que o antigo includes("@") + includes(".") deixava passar ("a@.a", "a@b..c").
const EMAIL_REGEX = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)*\.[A-Za-z]{2,}$/;

export function validateEmail(email: string) {
    return EMAIL_REGEX.test(email.trim());
}

// Telefone brasileiro: exatamente 11 dígitos (DDD + celular com o 9),
// que é o mesmo formato exigido por myArea.tsx e montado como +55{digitos} no phoneVerifier.
export function onlyDigits(value: string | undefined) {
    return (value || "").replace(/[^0-9]/g, '');
}

export function validatePhoneNumber(phoneNumber: string | undefined) {
    const digits = onlyDigits(phoneNumber);
    if (digits.length !== 11) {
        return false;
    }
    // DDD válido (11 a 99) e celular iniciando com 9
    const ddd = parseInt(digits.substring(0, 2));
    return ddd >= 11 && digits.charAt(2) === "9";
}

// Máscara (xx) xxxxx-xxxx aplicada enquanto o usuário digita.
export function maskPhoneNumber(value: string) {
    const digits = onlyDigits(value);
    switch (digits.length) {
        case 0:
            return "";
        case 1:
        case 2:
            return `(${digits}`;
        case 3:
        case 4:
        case 5:
        case 6:
            return `(${digits.substring(0, 2)}) ${digits.substring(2)}`;
        case 7:
        case 8:
        case 9:
        case 10:
            return `(${digits.substring(0, 2)}) ${digits.substring(2, 6)}-${digits.substring(6)}`;
        default:
            return `(${digits.substring(0, 2)}) ${digits.substring(2, 7)}-${digits.substring(7, 11)}`;
    }
}

// Política de senha do cadastro: mínimo 8 caracteres, com pelo menos uma letra e um número.
// Mais rígida que o mínimo de 6 do Firebase Auth. Retorna a mensagem do problema, ou null se estiver ok.
export const PASSWORD_RULE_TEXT = "A senha deve ter no mínimo 8 caracteres, incluindo ao menos uma letra e um número.";

export function validatePassword(password: string) {
    if (password.length < 8) {
        return PASSWORD_RULE_TEXT;
    }
    if (!/[A-Za-z]/.test(password) || !/[0-9]/.test(password)) {
        return PASSWORD_RULE_TEXT;
    }
    return null;
}

// Senha aleatória para contas criadas pelo admin (createUser.tsx). Usa crypto.getRandomValues
// (não Math.random) com rejeição para não enviesar o sorteio, só letras e números sem os
// caracteres ambíguos (0/O, 1/l/I) — a senha é repassada ao usuário por mensagem e digitada à mão.
// Garante minúscula, maiúscula e número, então sempre passa em validatePassword().
const PASSWORD_GROUPS = ["abcdefghijkmnopqrstuvwxyz", "ABCDEFGHJKLMNPQRSTUVWXYZ", "23456789"];

function randomIndex(max: number) {
    const limit = Math.floor(0x100000000 / max) * max;
    const buffer = new Uint32Array(1);
    do {
        crypto.getRandomValues(buffer);
    } while (buffer[0] >= limit);
    return buffer[0] % max;
}

export function generateSecurePassword(length = 14) {
    const all = PASSWORD_GROUPS.join("");
    const chars = PASSWORD_GROUPS.map(group => group[randomIndex(group.length)]);
    while (chars.length < length) {
        chars.push(all[randomIndex(all.length)]);
    }
    // embaralha (Fisher-Yates) para os caracteres obrigatórios não ficarem sempre no início
    for (let i = chars.length - 1; i > 0; i--) {
        const j = randomIndex(i + 1);
        [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    return chars.join("");
}

// Traduz o código de erro do Firebase Auth para uma mensagem em pt-BR (ver authErrorFields).
export function getAuthErrorMessage(errorCode: string | null | undefined, fallback = "Ops, algo deu errado! Tente novamente.") {
    if (!errorCode) {
        return fallback;
    }
    return authErrorFields[errorCode] || fallback;
}

// Máscara 111.111.111-11 aplicada enquanto o usuário digita.
// Mesma função usada para normalizar o CPF antes da checagem de unicidade,
// já que os documentos existentes guardam o valor COM máscara.
export function maskCPF(value: string | undefined) {
    const digits = onlyDigits(value).substring(0, 11);
    if (!digits.length) {
        return "";
    }
    if (digits.length <= 3) {
        return digits;
    }
    if (digits.length <= 6) {
        return `${digits.substring(0, 3)}.${digits.substring(3)}`;
    }
    if (digits.length <= 9) {
        return `${digits.substring(0, 3)}.${digits.substring(3, 6)}.${digits.substring(6)}`;
    }
    return `${digits.substring(0, 3)}.${digits.substring(3, 6)}.${digits.substring(6, 9)}-${digits.substring(9, 11)}`;
}

// Unicidade de CPF entre usuários. Os documentos guardam o valor COM máscara, mas contas
// antigas podem ter só os dígitos — por isso as duas formas são consultadas.
// Devolve undefined se a consulta falhar: quem chama deve abortar (fail closed).
export async function isCpfTaken(cpf: string) {
    const [hasMasked, hasDigits] = await Promise.all([
        collectionHasDoc("users", "cpf", maskCPF(cpf)),
        collectionHasDoc("users", "cpf", onlyDigits(cpf))
    ]);
    if (hasMasked === undefined || hasDigits === undefined) {
        return undefined;
    }
    return hasMasked || hasDigits;
}
